<template>
  <div class="article">
    <div>
      <div class="article-media">
        <img
          class="article-image"
          v-lazy="thumbUrl"
          :alt="article.title || 'News article'"
          preload="auto"
        />
        <span
          v-if="hasCoverageComparison"
          class="coverage-badge"
          :class="{ 'coverage-badge--political': article.coverageBiasMode === 'political' }"
          :aria-label="coverageBadgeAriaLabel"
          :title="coverageBadgeAriaLabel"
        >
          <span class="coverage-badge__icon" aria-hidden="true">
            <i class="fa fa-layer-group"></i>
          </span>
          <span class="coverage-badge__copy">
            <span class="coverage-badge__kicker">Media Intel</span>
            <span class="coverage-badge__value">{{ coverageBadgeText }}</span>
          </span>
        </span>
      </div>
      <p class="category">published: {{ timeAgo }}</p>
    </div>

    <a
      :href="articleHref"
      @click.prevent="emitNavigate"
    >
      <h3 class="title" @click="$emit('article-click', article.id)">{{ article.title }}</h3>
    </a>

    <!-- ── Sentiment + bias intel chips ─────────────────── -->
    <div
      v-if="sentimentToken || leanToken"
      class="article-intel-row"
    >
      <span
        v-if="sentimentToken"
        class="article-intel-chip"
        :style="sentimentTokenStyle"
        :aria-label="`Tone: ${sentimentToken.label}`"
      >
        <i :class="['fa', sentimentToken.icon, 'article-intel-chip__icon']" aria-hidden="true"></i>
        {{ sentimentToken.label }}
        <span v-if="isMockSentiment" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
      </span>
      <span
        v-if="leanToken"
        class="article-intel-chip article-intel-chip--bias"
        :style="leanTokenStyle"
        :aria-label="framingChipAria"
      >
        <span class="article-intel-chip__prefix" aria-hidden="true">Framing:</span>
        {{ displayBiasLabel }}
        <span v-if="isMockBias" class="article-intel-chip__mock" aria-hidden="true" title="Dev preview">·</span>
      </span>
    </div>

    <div v-if="article.matchedTopics?.length" class="matched-topics" aria-label="Topics that matched this article">
      <span
        v-for="topic in article.matchedTopics"
        :key="topic"
        class="topic-chip"
      >
        {{ topic }}
        <button
          class="topic-chip__remove"
          type="button"
          :aria-label="`Unfollow ${topic}`"
          title="Unfollow topic"
          @click.stop.prevent="$emit('unfollow-topic', topic)"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </span>
    </div>

    <!-- interaction buttons -->
    <div class="buttons">
      <!-- logged-out: point readers toward account creation -->
      <a href="/register" aria-label="Create a free account to like articles">
        <button aria-label="Like this article" v-if="!currentUser">
          <i class="fa fa-thumbs-up fa-regular"></i>
        </button>
      </a>
      <a href="/register" aria-label="Create a free account to dislike articles">
        <button aria-label="Dislike this article" v-if="!currentUser">
          <i class="fa fa-thumbs-down fa-regular"></i>
        </button>
      </a>
      <a href="/register" aria-label="Create a free account to save articles">
        <button aria-label="Save this article" v-if="!currentUser">
          <i class="fa fa-regular fa-bookmark"></i>
        </button>
      </a>

      <!-- logged-in: emit events to parent handler -->
      <button
        v-if="currentUser"
        :aria-label="article.viewerState?.liked ? 'Unlike this article' : 'Like this article'"
        @click="$emit('like', article.id)"
      >
        <i class="fa fa-thumbs-up fa-regular" :class="{ 'i-changed': article.viewerState?.liked }"></i>
      </button>
      <button
        v-if="currentUser"
        :aria-label="article.viewerState?.disliked ? 'Undislike this article' : 'Dislike this article'"
        @click="$emit('dislike', article.id)"
      >
        <i class="fa-regular fa-thumbs-down" :class="{ 'i-changed': article.viewerState?.disliked }"></i>
      </button>
      <button
        v-if="currentUser"
        :aria-label="article.viewerState?.savedForLater ? 'Unsave this article' : 'Save this article'"
        @click="$emit('save', article.id)"
      >
        <i class="fa fa-regular fa-bookmark" :class="{ 'i-changed': article.viewerState?.savedForLater }"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { optimizeImage } from '@/utils/imageOptimize';
import { getLeanToken, getSentimentToken } from '@/utils/coverageHelpers';
import {
  getFramingDisplayLabel,
  getFramingLeanToken,
  hasFramingFields,
} from '@/utils/framingHelpers';

const IS_DEV = process.env.NODE_ENV === 'development';

function devSeed(id) {
  return Number(id) || 0;
}

const BIAS_LABELS = ['Left-Leaning', 'Center', 'Right-Leaning'];

export default {
  name: 'ArticleCard',

  props: {
    article: {
      type: Object,
      required: true,
    },
    currentUser: {
      type: Number,
      default: null,
    },
  },

  emits: ['like', 'dislike', 'save', 'navigate', 'article-click', 'unfollow-topic'],

  computed: {
    thumbUrl() {
      return optimizeImage(this.article.imageUrl, 280, 158);
    },
    articleHref() {
      return this.article.urlPath;
    },
    hasCoverageComparison() {
      return Boolean(this.article.hasCoverageComparison);
    },
    coverageSourceCount() {
      const count = Number(this.article.coverageSourceCount);
      return Number.isFinite(count) && count > 0 ? Math.round(count) : null;
    },
    coverageBadgeText() {
      if (this.coverageSourceCount) {
        const noun = this.article.coverageBiasMode === 'political' ? 'perspectives' : 'sources';
        return `${this.coverageSourceCount} ${noun}`;
      }
      return 'Compare';
    },
    coverageBadgeAriaLabel() {
      if (this.coverageSourceCount) {
        return `Coverage comparison available with ${this.coverageBadgeText}`;
      }
      return 'Coverage comparison available';
    },
    timeAgo() {
      const publishedAt = this.article.publishedAt;
      if (!publishedAt) return '';
      const now = Date.now();
      const then = new Date(publishedAt).getTime();
      const diffMs = now - then;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
      if (diffHr > 0) return `${diffHr} hour${diffHr > 1 ? 's' : ''} ago`;
      if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
      return 'just now';
    },

    // ── Dev-only mock fallbacks ─────────────────────────────
    mockSentimentScore() {
      if (this.article.sentimentScore != null) return null;
      if (!IS_DEV) return null;
      const seed = devSeed(this.article.id);
      return ((seed * 9301 + 49297) % 233280) / 233280 * 100;
    },
    mockBiasLabel() {
      // Dev preview only when neither framing fields nor biasLabel are present.
      if (hasFramingFields(this.article)) return null;
      if (this.article.biasLabel != null) return null;
      if (!IS_DEV) return null;
      const seed = devSeed(this.article.id);
      return BIAS_LABELS[seed % BIAS_LABELS.length];
    },
    isMockSentiment() {
      return this.article.sentimentScore == null && this.mockSentimentScore != null;
    },
    isMockBias() {
      return !hasFramingFields(this.article)
        && this.article.biasLabel == null
        && this.mockBiasLabel != null;
    },
    hasArticleFraming() {
      return hasFramingFields(this.article);
    },

    // ── Real-or-mock display values ─────────────────────────
    displaySentimentScore() {
      const real = this.article.sentimentScore;
      return real != null ? Number(real) : this.mockSentimentScore;
    },
    displayBiasLabel() {
      // Prefer canonical framing when list/coverage responses include it.
      // Do not invent framing from categorySpecified (source cohort).
      if (this.hasArticleFraming) {
        return getFramingDisplayLabel(this.article) || this.article.framingLabel || this.article.biasLabel;
      }
      return this.article.biasLabel ?? this.mockBiasLabel;
    },
    framingChipAria() {
      if (this.hasArticleFraming) {
        return `Framing: ${this.displayBiasLabel || 'unknown'}`;
      }
      return `Framing: ${this.displayBiasLabel || 'unknown'}`;
    },
    sentimentToken() {
      return getSentimentToken(this.displaySentimentScore);
    },
    leanToken() {
      if (this.hasArticleFraming) {
        return getFramingLeanToken(this.article);
      }
      // Legacy / mock path — exact label keys only; no cohort inference
      return getLeanToken(this.displayBiasLabel);
    },
    sentimentTokenStyle() {
      return this.tokenStyle(this.sentimentToken);
    },
    leanTokenStyle() {
      return this.tokenStyle(this.leanToken);
    },
  },

  methods: {
    tokenStyle(token) {
      if (!token) return {};
      return {
        color: token.color,
        background: token.bg,
        border: `1px solid ${token.border}`,
      };
    },
    emitNavigate() {
      const urlPath = this.article.urlPath;
      if (typeof urlPath === 'string' && urlPath.startsWith('/')) {
        this.$emit('navigate', urlPath);
        return;
      }
      this.$emit('navigate', this.article.categorySpecified, this.article.title, this.article.id);
    },
  },
};
</script>

<style scoped>
  a{
       text-decoration: none;
       color: black;
       transition: all 0.3s ease;
       font-family: 'Titillium Web', sans-serif;
     }
     a:hover{
       color: #3088d4;
     }

     .hidden {
         display: none;
     }
   
     i {
       margin: 9px 5px 0px 5px;
       color: #0b7ee3;
     }
     i:hover{
       color: #dd3434;
     }
   
     .i-changed {
       color:#dd3434
     }

     button{
       background: none;
       border: none;
       cursor: pointer;
     }

     /* ARTICLE */
   
     .article{
     padding-bottom: 20px;
     border-bottom: 1px solid #dd3434;
     margin-left: 5%;
     height: fit-content;
     width: 80%;
     background-color: #fff;
     text-align: center;
     transition: all 0.3s ease;
     animation: fadeInBottom 0.5s ease-in;
   }
   
   @keyframes fadeInBottom {
     from {
         transform: translateY(100%);
         opacity: 0;
     }
     to {
         transform: translateY(0);
         opacity: 1;
     }
 }
   
   
     .article:hover,
     .article.active{
       border-bottom: 2px solid #0b7ee3;
       background-color: #eef6ff;
       color: #3088d4;
       transition: border-bottom 0.3s ease, color 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
       transform: scale(1.01);
       box-shadow: 0 8px 22px rgba(11, 126, 227, 0.18);
       opacity: 1;
     }

     .article:hover a,
     .article.active a {
       color: #3088d4;
     }
   
    .article-media {
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      border: 1px solid rgba(148, 163, 184, 0.16);
      background: #ffffff;
    }

    .article-image{
      display: block;
       height: 57%;
       width:100%;
       aspect-ratio: 16/9;
       object-fit: cover;
     }

    .coverage-badge {
      position: absolute;
      right: 9px;
      bottom: 9px;
      display: inline-flex;
      align-items: center;
      gap: 7px;
      max-width: calc(100% - 18px);
      padding: 7px 9px;
      border: 1px solid rgba(255, 255, 255, 0.38);
      border-radius: 999px;
      background:
        linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.82)),
        rgba(15, 23, 42, 0.86);
      color: #ffffff;
      box-shadow: 0 12px 28px rgba(2, 6, 23, 0.26);
      backdrop-filter: blur(12px);
      transform: translateZ(0);
    }

    .coverage-badge--political {
      background:
        linear-gradient(135deg, rgba(30, 64, 175, 0.92), rgba(127, 29, 29, 0.82)),
        rgba(15, 23, 42, 0.86);
    }

    .coverage-badge__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      flex: 0 0 22px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.16);
    }

    .coverage-badge__icon i,
    .coverage-badge__icon i:hover {
      margin: 0;
      color: #ffffff;
      font-size: 0.72rem;
    }

    .coverage-badge__copy {
      display: inline-flex;
      min-width: 0;
      flex-direction: column;
      gap: 1px;
      line-height: 1;
    }

    .coverage-badge__kicker {
      color: rgba(255, 255, 255, 0.72);
      font-size: 0.54rem;
      font-weight: 950;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      white-space: nowrap;
    }

    .coverage-badge__value {
      color: #ffffff;
      font-size: 0.72rem;
      font-weight: 950;
      white-space: nowrap;
    }
   
     .title{
       margin: 0px;
       cursor: pointer;
       overflow-wrap: break-word;
       word-break: break-word;
     }
    
     .buttons{
       display: flex;
       justify-content: center;
     }

     .matched-topics {
       display: flex;
       flex-wrap: wrap;
       gap: 6px;
       margin: 10px 8px 4px;
     }

     .topic-chip {
       display: inline-flex;
       align-items: center;
       gap: 5px;
       max-width: 100%;
       padding: 4px 8px 4px 10px;
       border-radius: 999px;
       background: linear-gradient(135deg, #eef6ff 0%, #f8fbff 100%);
       border: 1px solid rgba(11, 126, 227, 0.22);
       color: #0b65c2;
       font-size: 0.68rem;
       font-weight: 800;
       line-height: 1.1;
       box-shadow: 0 1px 3px rgba(11, 126, 227, 0.08);
       overflow-wrap: anywhere;
     }

     .topic-chip__remove {
       display: inline-flex;
       align-items: center;
       justify-content: center;
       width: 16px;
       height: 16px;
       padding: 0;
       border-radius: 50%;
       background: rgba(11, 126, 227, 0.1);
       color: #0b65c2;
       transition: background 0.15s, color 0.15s, transform 0.15s;
     }

     .topic-chip__remove:hover {
       background: #dd3434;
       color: #fff;
       transform: scale(1.06);
     }

     .topic-chip__remove i {
       margin: 0;
       color: inherit;
       font-size: 0.58rem;
     }

     @media (max-width: 913px){
   .buttons{
     display: flex;
     justify-content: center;
   }
 }
     
     .category{
       font-size: 0.72rem;
       display: inline-block;
       margin: 4px 0 8px;
       z-index: 1;
       background: #e4e6eb;
       border-radius: 10%;
       padding: 2px;
       text-align: center;
     }

  @media (max-width: 649px) {
  .article{
        width: 160px !important;
      }
  .article {
    box-sizing: border-box;
    transition: transform 0.3s ease;
    margin-left: 5%;
  }

  .coverage-badge {
    right: 7px;
    bottom: 7px;
    gap: 5px;
    padding: 6px 7px;
  }

  .coverage-badge__icon {
    width: 18px;
    height: 18px;
    flex-basis: 18px;
  }

  .coverage-badge__icon i,
  .coverage-badge__icon i:hover {
    font-size: 0.62rem;
  }

  .coverage-badge__kicker {
    display: none;
  }

  .coverage-badge__value {
    font-size: 0.66rem;
  }
  }

  /* ── Sentiment + bias intel chips ──────────────────── */
  .article-intel-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
    padding: 6px 8px 2px;
  }

  .article-intel-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.025em;
    line-height: 1.3;
    white-space: nowrap;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.66);
  }

  .article-intel-chip__icon,
  .article-intel-chip__icon:hover {
    margin: 0 3px 0 0;
    color: currentColor;
    font-size: 0.6rem;
  }

  .article-intel-chip__prefix {
    font-weight: 700;
    opacity: 0.75;
  }

  .article-intel-chip__mock {
    font-size: 0.6rem;
    opacity: 0.45;
    cursor: help;
    margin-left: 1px;
  }

  @media (max-width: 649px) {
    .article-intel-chip {
      font-size: 0.62rem;
      padding: 2px 7px;
    }
  }

  @media (max-width: 693px) {
  button i {
    font-size: small;
  }

  .title{
    font-size: large;
  }
  }

  @media screen and (max-width: 398px) {

  .article{
    margin-left: 0%;
  }

  }
</style>
